import axios from "axios";
import {format} from "date-fns";
import {BookingInterface} from "../types/types";
import {getBookingConfig} from "../data/price";
import {HolidaysManager} from "../data/HolidaysManager";
import {RentalPointDelivery} from "../data/RentalPointDelivery";
import store from "../store";
import { flattenObjectOneLevel } from "../components/utils/stateHelper";
import { setBookingStateFromFlat } from "../stateReducers/gunOptionSlice";
import { TFDeliveryService } from "./TFDeliveryService";
import { DPDDeliveryService } from "./DPDDeliveryService";


export default class BookingService{

    static checkRentalPointAvailability(gunType: string, playStart: Date, playEnd: Date, quantity: number) {
        const bookingConfig = getBookingConfig();
        const holidayManager = new HolidaysManager(bookingConfig, bookingConfig.rentalPointSchedule);
        const rentalPointsScheduler = new RentalPointDelivery(holidayManager);
        let payload: { [k: string]: any } = {
            quantity: quantity,
            gunType: gunType,
            clickedDate: format(playStart, "yyyy-MM-dd")
        };
        if ("brx" === gunType || quantity > bookingConfig.INumberOfLaserGunsNotAllowedToReserveInRentalPoints) {
            payload['dates'] = rentalPointsScheduler.getPlayDatesForOurPoints(playStart, playEnd);
        } else {
            payload['dates'] = rentalPointsScheduler.getPlayDatesForAllPoints(playStart, playEnd);
        }

        payload['clickedDate'] = format(playStart, "yyyy-MM-dd");
        return axios.get('admin-ajax.php?action=new_booking_check_availability2_in_rental_points', {params: payload});
    }

    static checkGivenRentalPointAvailability(
            point: number,
            gunType: string,
            quantity: number,
            startDate: Date,
            endDate: Date,
            playStart: Date // Used for validating that the most recent request is applied
    ) {

        let payload: { [k: string]: any } = {
            quantity: quantity,
            gunType: gunType,
            dates: [
                {
                    point: point,
                    startDate: format(startDate, "yyyy-MM-dd"),
                    endDate: format(endDate, "yyyy-MM-dd"),
                }
            ],
            // Used for validating that the most recent request is applied
            clickedDate: format(playStart, "yyyy-MM-dd")
        };

        return axios.get('admin-ajax.php?action=new_booking_check_availability2_in_rental_points', {params: payload});
    }

    static async checkAllAvailability(gunType: string, playStart: Date, playEnd: Date, quantity: number) {
        const bookingConfig = getBookingConfig();
        const holidayManager = new HolidaysManager(bookingConfig, bookingConfig.rentalPointSchedule);
        let dpdRequest = BookingService.checkDPDAvailabilityForPeriod(gunType, quantity, playStart, playEnd);
        let rentalPointRequest = this.checkRentalPointAvailability(gunType, playStart, playEnd, quantity);

        const country = bookingConfig['country'] ?? 'be';
        if (country !== 'be') {
            return new Promise((resolve, reject) => {
                Promise.all([dpdRequest, rentalPointRequest]).then(([dpdResponse, rentalResponse]) => {
                    const resolveObj = {
                        transforce: false,
                        dpd: dpdResponse.data.delivery,
                        ...rentalResponse.data
                    };
                    
                    resolve(resolveObj);
                }).catch(reject);
            });
        }else{
            let transForceRequest = BookingService.checkTransforceAvailabilityForPeriod(gunType, quantity, playStart, playEnd);

            return new Promise((resolve, reject) => {
                Promise.all([transForceRequest, dpdRequest, rentalPointRequest]).then(([transForceResponse, dpdResponse, rentalResponse]) => {
                    const resolveObj = {
                        transforce: transForceResponse.data.delivery,
                        dpd: dpdResponse.data.delivery,
                        ...rentalResponse.data
                    }
                    resolve(resolveObj);
                }).catch(reject);
            });
        }
    }

    static async checkTransforceAvailabilityForPeriod(gunType: string, quantity: number, playStart: Date, playEnd: Date) {
        // The latest that the package must be given to Transforce for the given playStart
        const startDate = TFDeliveryService.latestShippingDateBefore(playStart);

        // The soonest day that the package can be picked up by Transforce
        const returnDate = TFDeliveryService.earliestReturnDateAfter(playEnd);

        // The day that the package is received back at a rental point
        const endDate = TFDeliveryService.endDateByReturnDate(returnDate);
        return this.checkAvailabilityForPeriodAndDelivery("transforce", gunType, quantity, startDate, endDate, playStart);
    }

    static async checkDPDAvailabilityForPeriod(gunType: string, quantity: number, playStart: Date, playEnd: Date) {
        // The latest that the package must be given to DPD for the given playStart
        let startDate = DPDDeliveryService.latestShippingDateBefore(playStart);

        // The soonest day that the package can be returned to a DPD point for the given PlayEnd
        let returnToDPDPointDate = DPDDeliveryService.earliestReturnDateAfter(playEnd);

        // The day that the package is received back at a rental point
        let endDate = DPDDeliveryService.endDateByReturnDate(returnToDPDPointDate);
        
        // Call the backend        
        return this.checkAvailabilityForPeriodAndDelivery("dpd", gunType, quantity, startDate, endDate, playStart);
    }

    static checkAvailabilityForPeriodAndDelivery(
            delivery: string, gunType: string, quantity: number, startDate: Date, endDate: Date,
            clickedDate: Date // Used for validating that the most recent request is applied
    ) {
        return axios.get('admin-ajax.php', {
            params: {
                action: "new_booking_check_availability2_for_period",
                gunType: gunType,
                delivery: delivery,
                startDate: format(startDate, "yyyy-MM-dd"),
                endDate: format(endDate, "yyyy-MM-dd"),
                quantity: quantity,
                // Used for validating that the most recent request is applied
                clickedDate: format(clickedDate, "yyyy-MM-dd")
            }
        });
    }

    static async makeBooking(data: BookingInterface) {
        let payload: { [k: string]: any } = {};
        for (const [k, v] of Object.entries(data)) {
            if (v instanceof Date) {
                //convert dates to strings without timezone stuff
                payload[k] = format(v, "yyyy-MM-dd");
            } else {
                payload[k] = v as any;
            }
        }
        return await axios.post("admin-ajax.php?action=new_booking_make_reservation2", payload);
    }

    static async checkExtrasAvailability(data: BookingInterface) {
        return await axios.get("admin-ajax.php?action=new_booking_check_extras_availability2", {
            params: {
                gunType: data.gunType,
                deliveryDate: format(data.deliveryDate || new Date(), "yyyy-MM-dd"),
                returnDate: format(data.returnDate || new Date(), "yyyy-MM-dd"),
                playStart: format(data.playStart || new Date(), "yyyy-MM-dd"),
                playEnd: format(data.playEnd || new Date(), "yyyy-MM-dd"),
                startDate: format(data.startDate || new Date(), "yyyy-MM-dd"),
                endDate: format(data.endDate || new Date(), "yyyy-MM-dd"),
                deliveryType: data.deliveryType,
                rentalPoint: data.rentalPoint
            }
        });
    }

    static async getDPDPoints(lat: number, lng: number, dateToCheck: Date) {
        return await axios.get('admin-ajax.php', {
            params: {
                action: "new_booking_get_dpd_points",
                lat: lat,
                lng: lng,
                date: format(dateToCheck, "yyyy-MM-dd")
            }
        });
    }

    /**
     * @returns "No change" | "Extras added" | "Invalid coupon" | "Not enough stock" | "Error applying coupon" | "Unknown coupon code",
     */
    static async handleCouponClick(): Promise<string> {
        const bookingStateSnapshot = flattenObjectOneLevel(store.getState()) as BookingInterface;
        let bookingStatePayload: { [key: string]: any } = {};
        for (const [key, value] of Object.entries(bookingStateSnapshot)) {
            if (value instanceof Date) {
                //convert dates to strings without timezone stuff
                bookingStatePayload[key] = format(value, "yyyy-MM-dd");
            } else {
                bookingStatePayload[key] = value as any;
            }
        }
        const payload = {
            "bookingState": bookingStatePayload,
            "couponCode": bookingStateSnapshot.coupon
        };
        return await axios.post(
            'admin-ajax.php?action=handle_coupon_code_click',
            payload
        )
        /**
         * Copied from functions.php `handle_coupon_code_click_callback()` docstring
         * @return [
         *      returnMessage => "No change" | "Extras added" | "Invalid coupon" | "Not enough stock | "Unknown coupon code",
         *      bookingState
         * ]
         */
        .then((response) => {
            const returnMessage: string = response.data.returnMessage;
            switch (returnMessage) {
                case "Extras added":
                    store.dispatch(setBookingStateFromFlat(response.data.bookingState!));
                    return "Extras added";
                case "No change":
                case "Invalid coupon":
                case "Not enough stock":
                case "Unknown coupon code":
                    return response.data.returnMessage;
                default:
                    console.error("Ran into default returnMessage case: (" + returnMessage + ")");
                    return "Error applying coupon";
            }
        },
        // Failed
        (reason) => {
            return { "message": "Error applying coupon" };
        })
        .catch((error: Error) => {
            console.error(`Error: ${error}`);
            return { "message": "Error applying coupon" };
        });
    }

    static async getCheckfrontIFrameUrl(): Promise<string|null> {
        const bookingStateSnapshot = flattenObjectOneLevel(store.getState()) as BookingInterface;
        let bookingStatePayload: { [key: string]: any } = {};
        for (const [key, value] of Object.entries(bookingStateSnapshot)) {
            if (value instanceof Date) {
                //convert dates to strings without timezone stuff
                bookingStatePayload[key] = format(value, "yyyy-MM-dd");
            } else {
                bookingStatePayload[key] = value as any;
            }
        }
        const payload = {
            "bookingState": bookingStatePayload
        };
        return await axios.post(
            'admin-ajax.php?action=checkfront_iframe_url_from_booking_state',
            payload
        )
        .then((response) => {
            console.trace(JSON.stringify(response));
            return response.data.link;
        },
        // Failed
        (reason) => {
            return null;
        })
        .catch((error: Error) => {
            console.log(error);
            return null;
        });
    }

    static async checkCheckfrontConversionSafe(bookingCode: string): Promise<boolean> {
        const payload = {
            'booking_code': bookingCode
        };
        return await axios.post(
            'admin-ajax.php?action=checkfront_conversion_middleware_callback',
            payload
        )
        .then((response) => {
            // Format for successful response as of March 4th, 2024:
            // {
            //     safe: boolean,
            //     checkfront_error: boolean,
            //     data_error: boolean
            // }
            console.trace(JSON.stringify(response));
            if (response.data.safe && response.data.safe == true) {
                return true;
            } else {
                return false;
            }
        },
        // Failed
        (reason) => {
            return false;
        })
        .catch((error: Error) => {
            console.log(error);
            return false;
        });
    }
}
